import React from "react";
import { LinearProgress } from "@mui/material";
import { SovosBox, SovosIconButton, SovosPopover, SovosTypography } from "mosaic-react";
import { ArrowForward, Close } from "mosaic-react-icons";
import { SovosAIIcon } from "mosaic-react-platform";
import {
    AI_BRAND_GRADIENT_ICON_LEAD,
    aiBrandLinearGradient315,
} from "mosaic-react-platform/shared/aiBrandGradient";
import AiConfidencePill from "./AiConfidencePill";
import { toSourceColumnLabel } from "../../mappingDisplayLabels";

const ColumnMappingAiSuggestionPopover = ({
    anchorEl,
    open,
    onClose,
    sourceColumn,
    targetLabel,
    score,
    rationale,
    i18n = (strings) => (Array.isArray(strings) ? strings.join("") : strings),
}) => {
    const pct = Math.round((score ?? 0) * 100);

    return (
        <SovosPopover
            open={open}
            anchorEl={anchorEl}
            onClose={onClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
            transformOrigin={{ vertical: "top", horizontal: "left" }}
            PaperProps={{ sx: { maxWidth: 380, p: 2 } }}
        >
            <SovosBox sx={{ alignItems: "center", display: "flex", justifyContent: "space-between", mb: 1.5 }}>
                <SovosBox sx={{ alignItems: "center", display: "flex", gap: 1 }}>
                    <SovosBox sx={{ alignItems: "center", display: "flex", "& svg": { height: 20, width: 20 } }}>
                        <SovosAIIcon />
                    </SovosBox>
                    <SovosTypography variant="subtitle2" fontWeight={700}>
                        {i18n`Column Mapping suggestions by Sovi`}
                    </SovosTypography>
                </SovosBox>
                <SovosIconButton size="small" tooltipText={i18n`Close`} onClick={onClose}>
                    <Close sx={{ fontSize: 16 }} />
                </SovosIconButton>
            </SovosBox>

            <SovosBox sx={{ alignItems: "center", display: "flex", gap: 1, mb: 1.5 }}>
                <SovosBox sx={{ minWidth: 0 }}>
                    <SovosTypography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {i18n`Source column`}
                    </SovosTypography>
                    <SovosTypography variant="body2" fontWeight={600} noWrap>
                        {toSourceColumnLabel(sourceColumn)}
                    </SovosTypography>
                </SovosBox>
                <ArrowForward sx={{ color: "text.disabled", fontSize: 16, flexShrink: 0 }} />
                <SovosBox sx={{ minWidth: 0 }}>
                    <SovosTypography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        {i18n`SAF-T column`}
                    </SovosTypography>
                    <SovosTypography variant="body2" color="primary.main" fontWeight={600} noWrap>
                        {targetLabel}
                    </SovosTypography>
                </SovosBox>
            </SovosBox>

            <SovosTypography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.75 }}>
                {i18n`Confidence score`}
            </SovosTypography>
            <SovosBox sx={{ alignItems: "center", display: "flex", gap: 1, mb: 0.75 }}>
                <AiConfidencePill score={score} />
                <SovosTypography variant="caption" color="text.secondary">
                    {i18n`${pct}% confidence`}
                </SovosTypography>
            </SovosBox>
            <LinearProgress
                variant="determinate"
                value={pct}
                sx={{
                    height: 6,
                    borderRadius: 999,
                    mb: 1.5,
                    bgcolor: "#FCE7F3",
                    "& .MuiLinearProgress-bar": {
                        background: aiBrandLinearGradient315(AI_BRAND_GRADIENT_ICON_LEAD),
                        borderRadius: 999,
                    },
                }}
            />

            <SovosTypography variant="caption" color="text.secondary" fontWeight={600} sx={{ display: "block", mb: 0.5 }}>
                {i18n`AI Reasoning`}
            </SovosTypography>
            <SovosTypography variant="body2" color="text.primary">
                {rationale || i18n`AI matched this column by semantic similarity.`}
            </SovosTypography>
        </SovosPopover>
    );
};

export default ColumnMappingAiSuggestionPopover;
